import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { apiFetch, apiRequest } from '../lib/api';
import {
  markAuthBootstrapComplete,
  markAuthBootstrapStarted,
  resetAuthBootstrap,
} from '../lib/authBootstrap';
import { AUTH_SESSION_INVALIDATED_EVENT } from '../lib/authSessionEvents';
import type { AuthUser } from '../types/roles';
import {
  clearAllStoredAuthTokens,
  clearTenantScope,
  clearPersistedAuthState,
  clearSessionPersistencePreference,
  getBestAuthToken,
  getBestRefreshToken,
  getTenantScope,
  persistAccessToken,
  persistRefreshToken,
  setActivePartitionScope,
  setOrganizationScope,
  setSessionPersistencePreference,
} from '../utils/authHeaders';
import { refreshAccessToken } from '../utils/refreshAccessToken';

interface AuthContextType {
  user: AuthUser | null;
  login: (email: string, password: string, rememberDevice?: boolean) => Promise<void>;
  completeMfaChallenge: (mfaToken: string, code: string) => Promise<string>;
  register: (userData: RegisterData, rememberDevice?: boolean) => Promise<void>;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
}

interface RegisterData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  organization_name: string;
  organization_slug: string;
}

interface AuthTokenResponse {
  access_token?: string;
  refresh_token?: string;
  user?: AuthUser;
  mfa_required?: boolean;
  mfa_enrollment_required?: boolean;
  mfa_token?: string;
}

/**
 * Thrown by login() when the account requires a second factor. The login form
 * catches this to switch to the MFA challenge screen instead of treating it as
 * an error.
 *
 * IMPORTANT: this app lazy-loads route chunks, and auth modules can be
 * duplicated across chunks (see the useAuth guard below). That makes
 * `instanceof MfaRequiredError` unreliable across a chunk boundary, so we also
 * stamp a stable marker property and expose a marker-based type guard.
 */
export class MfaRequiredError extends Error {
  /** Stable discriminator that survives module duplication across bundle chunks. */
  readonly isMfaRequiredError = true as const;
  mfaToken: string;
  enrollmentRequired: boolean;
  constructor(mfaToken: string, enrollmentRequired: boolean = false) {
    super('MFA_REQUIRED');
    this.name = 'MfaRequiredError';
    this.mfaToken = mfaToken;
    this.enrollmentRequired = enrollmentRequired;
  }
}

/**
 * Chunk-safe check for an MFA-required error. Use this instead of `instanceof`,
 * which fails when AuthContext is duplicated across lazy chunks.
 */
export function isMfaRequiredError(
  error: unknown
): error is MfaRequiredError {
  return Boolean(
    error &&
      typeof error === 'object' &&
      (error as { isMfaRequiredError?: boolean }).isMfaRequiredError === true
  );
}

type JwtPayload = {
  exp?: number;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const decodeJwtPayload = (accessToken: string): JwtPayload | null => {
  try {
    return JSON.parse(atob(accessToken.split('.')[1])) as JwtPayload;
  } catch {
    return null;
  }
};

const isJwtExpired = (payload: JwtPayload | null): boolean => {
  if (!payload) return true;
  if (!payload.exp) return false;
  return payload.exp * 1000 < Date.now();
};

function storeAuthTokens(accessToken: string, refreshToken?: string): void {
  persistAccessToken(accessToken);
  if (refreshToken) {
    persistRefreshToken(refreshToken);
  }
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const applyUserContext = useCallback((nextUser: AuthUser): AuthUser => {
    const existingScope = getTenantScope().organizationId;
    if (!existingScope && nextUser.organization_id) {
      setOrganizationScope(nextUser.organization_id);
    }
    if (nextUser.role === 'agency_operator') {
      const existingActivePartition = getTenantScope().activePartitionId;
      const assignedPartitions = nextUser.assigned_partition_ids ?? [];
      if (assignedPartitions.length === 1) {
        setActivePartitionScope(assignedPartitions[0]);
      } else if (existingActivePartition && !assignedPartitions.includes(existingActivePartition)) {
        setActivePartitionScope(null);
      }
    } else if (nextUser.partition_id) {
      setActivePartitionScope(nextUser.partition_id);
    } else {
      setActivePartitionScope(null);
    }

    setUser(nextUser);
    return nextUser;
  }, []);

  const logout = useCallback((): void => {
    try {
      clearAllStoredAuthTokens();
      clearSessionPersistencePreference();
      clearPersistedAuthState();
      clearTenantScope();
      setUser(null);
      markAuthBootstrapComplete(false);
    } catch (error) {
      console.error('Logout error:', error);
      clearPersistedAuthState();
      clearTenantScope();
      setUser(null);
      resetAuthBootstrap();
      markAuthBootstrapComplete(false);
    }
  }, []);

  const loadCurrentUser = useCallback(async (): Promise<AuthUser> => {
    const me = await apiFetch('/api/v1/auth/me');
    const normalizedUser = me as AuthUser;
    return applyUserContext(normalizedUser);
  }, [applyUserContext]);

  useEffect(() => {
    const initializeAuth = async () => {
      markAuthBootstrapStarted();

      let token = getBestAuthToken();
      if (!token) {
        setUser(null);
        markAuthBootstrapComplete(false);
        setLoading(false);
        return;
      }

      const decoded = decodeJwtPayload(token);
      if (isJwtExpired(decoded)) {
        const refreshToken = getBestRefreshToken();
        if (!refreshToken) {
          clearPersistedAuthState();
          setUser(null);
          markAuthBootstrapComplete(false);
          setLoading(false);
          return;
        }

        const refreshed = await refreshAccessToken();
        if (!refreshed) {
          clearPersistedAuthState();
          setUser(null);
          markAuthBootstrapComplete(false);
          setLoading(false);
          return;
        }
        token = refreshed;
      }

      try {
        await loadCurrentUser();
        markAuthBootstrapComplete(true);
      } catch (error) {
        console.error('Auth initialization failed:', error);
        clearPersistedAuthState();
        setUser(null);
        markAuthBootstrapComplete(false);
      } finally {
        setLoading(false);
      }
    };

    void initializeAuth();
  }, [loadCurrentUser]);

  useEffect(() => {
    const handleSessionInvalidated = () => {
      clearPersistedAuthState();
      clearTenantScope();
      setUser(null);
      markAuthBootstrapComplete(false);
      setLoading(false);
    };

    window.addEventListener(AUTH_SESSION_INVALIDATED_EVENT, handleSessionInvalidated);
    return () => {
      window.removeEventListener(AUTH_SESSION_INVALIDATED_EVENT, handleSessionInvalidated);
    };
  }, []);

  const login = async (
    email: string,
    password: string,
    rememberDevice: boolean = true
  ): Promise<void> => {
    if (!email?.trim() || !password?.trim()) {
      throw new Error('Email and password are required');
    }

    try {
      const authResponse = (await apiFetch('/api/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      })) as AuthTokenResponse;

      // Second-factor gate: the server withholds tokens and asks for a code.
      if (authResponse.mfa_required && authResponse.mfa_token) {
        setSessionPersistencePreference(rememberDevice);
        throw new MfaRequiredError(
          authResponse.mfa_token,
          Boolean(authResponse.mfa_enrollment_required)
        );
      }

      const { access_token, refresh_token, user: authUser } = authResponse;
      if (!access_token) {
        throw new Error('Invalid response from server');
      }

      setSessionPersistencePreference(rememberDevice);
      storeAuthTokens(access_token, refresh_token);
      if (authUser) {
        applyUserContext(authUser);
        markAuthBootstrapComplete(true);
      } else {
        await loadCurrentUser();
        markAuthBootstrapComplete(true);
      }

      // Emit login-complete event with role-based target so callers can redirect
      try {
        const payload = JSON.parse(atob(access_token.split('.')[1]));
        const role = payload.role;
        let target = '/dashboard';
        if (role === 'partner_user') target = '/partner/dashboard';
        else if (role === 'agency_operator') target = '/campaigns';
        // Store target for callers to read synchronously
        (window as any).__authLoginTarget = target;
        window.dispatchEvent(new CustomEvent('auth:login-complete', { detail: { role, target } }));
      } catch { /* non-critical */ }
    } catch (error) {
      // MFA challenge is an expected branch, not a failure: leave state intact
      // so the challenge screen can complete the login.
      if (isMfaRequiredError(error)) {
        throw error;
      }
      console.error('Login failed:', error);
      clearPersistedAuthState();
      setUser(null);
      markAuthBootstrapComplete(false);
      throw error;
    }
  };

  /**
   * Complete an MFA challenge: exchange the challenge token + code for real
   * tokens and finish establishing the session. Returns the role-based redirect
   * target, mirroring what login() computes.
   */
  const completeMfaChallenge = async (
    mfaToken: string,
    code: string
  ): Promise<string> => {
    const authResponse = (await apiFetch('/api/v1/auth/mfa/challenge', {
      method: 'POST',
      body: JSON.stringify({ mfa_token: mfaToken, code: code.trim() }),
    })) as AuthTokenResponse;

    const { access_token, refresh_token, user: authUser } = authResponse;
    if (!access_token) {
      throw new Error('Invalid response from server');
    }

    storeAuthTokens(access_token, refresh_token);
    if (authUser) {
      applyUserContext(authUser);
      markAuthBootstrapComplete(true);
    } else {
      await loadCurrentUser();
      markAuthBootstrapComplete(true);
    }

    let target = '/dashboard';
    try {
      const payload = JSON.parse(atob(access_token.split('.')[1]));
      const role = payload.role;
      if (role === 'partner_user') target = '/partner/dashboard';
      else if (role === 'agency_operator') target = '/campaigns';
      window.dispatchEvent(new CustomEvent('auth:login-complete', { detail: { role, target } }));
    } catch { /* non-critical */ }
    return target;
  };

  const register = async (
    userData: RegisterData,
    rememberDevice: boolean = true
  ): Promise<void> => {
    if (
      !userData?.email?.trim() ||
      !userData?.password?.trim() ||
      !userData?.first_name?.trim() ||
      !userData?.last_name?.trim() ||
      !userData?.organization_name?.trim() ||
      !userData?.organization_slug?.trim()
    ) {
      throw new Error('All fields are required');
    }

    try {
      const cleanedData = {
        email: userData.email.trim().toLowerCase(),
        password: userData.password,
        first_name: userData.first_name.trim(),
        last_name: userData.last_name.trim(),
        organization_name: userData.organization_name.trim(),
        organization_slug: userData.organization_slug.trim().toLowerCase(),
      };

      const authResponse = (await apiFetch('/api/v1/auth/register', {
        method: 'POST',
        body: JSON.stringify(cleanedData),
      })) as AuthTokenResponse;

      const { access_token, refresh_token, user: authUser } = authResponse;
      if (!access_token) {
        throw new Error('Invalid response from server');
      }

      setSessionPersistencePreference(rememberDevice);
      storeAuthTokens(access_token, refresh_token);
      if (authUser) {
        applyUserContext(authUser);
        markAuthBootstrapComplete(true);
      } else {
        await loadCurrentUser();
        markAuthBootstrapComplete(true);
      }
    } catch (error) {
      console.error('Registration failed:', error);
      clearPersistedAuthState();
      setUser(null);
      markAuthBootstrapComplete(false);
      throw error;
    }
  };

  const authFetch = useCallback(async (url: string, options: RequestInit = {}): Promise<Response> => {
    return apiRequest(url, options);
  }, []);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      login,
      completeMfaChallenge,
      register,
      logout,
      loading,
      isAuthenticated: Boolean(user),
      authFetch,
    }),
    [authFetch, loading, login, completeMfaChallenge, logout, register, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error(
      'useAuth must be used within AuthProvider. If this appears on /login, auth modules may be duplicated by lazy route chunks.'
    );
  }
  return context;
};
